import React, { useEffect, useState } from "react";
import ExportButton from "./ExportButton";
import { useListParams } from "hooks/useListParams";
import Pagination from "./common/Pagination";
import Drawer, { FilterDrawer } from "./common/Drawer";
import SendEmailSidebar from "./SendEmailSidebar";
import { apiKeyLabel, useApiKeyNames } from "hooks/useApiKeyNames";
import ApiKeySelect from "./common/ApiKeySelect";
import { useParams } from "react-router-dom";
import { CustomerService } from "services/CustomerService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GetEmailsDto } from "services/Dtos/GetEmailsDto";
import EmailHistorySidebar from "./EmailHistorySidebar";

type SortField =
  | "recipientEmail"
  | "senderEmail"
  | "subject"
  | "timestamp"
  | "lastStatus"
  | "createdAt";

interface Props {
  customerId?: string;
}

const PAGE_SIZE = 10;

const emptyFilters = { apiKeyId: '', recipientEmails: '', senderEmails: '', subject: '', status: '', createdFrom: '', createdTo: '' };

// The key the email was sent with; older responses only carried the recipient's key.
const sentWithApiKeyId = (email: GetEmailsDto) => email.apiKeyId ?? email.recipientEmail?.apiKeyId;

const CustomerEmailMessages: React.FC<Props> = ({ customerId: propCustomerId }) => {
  const { customerId: paramCustomerId } = useParams<{ customerId: string }>();
  const customerId = propCustomerId ?? paramCustomerId;
  const [emails, setEmails] = useState<GetEmailsDto[]>([]);
  const apiKeyNames = useApiKeyNames(emails.map(sentWithApiKeyId));
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { pageNumber, setPageNumber, sortField, sortDirection, setSort, appliedFilters, setAppliedFilters } =
    useListParams<typeof emptyFilters, SortField>(emptyFilters, { field: "createdAt", direction: "desc" });
  const [selectedEmail, setSelectedEmail] = useState<GetEmailsDto | null>(null);
  const [previewEmail, setPreviewEmail] = useState<GetEmailsDto | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isSendOpen, setIsSendOpen] = useState(false);

  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [selectedEmailForBlock, setSelectedEmailForBlock] = useState<{
    email: string;
    isBlocked: boolean;
    apiKeyId: number;
  } | null>(null);
  const [blockReason, setBlockReason] = useState("");

  // Filter sidebar state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(emptyFilters);

  useEffect(() => {
    setTempFilters({ ...appliedFilters });
  }, [isFilterOpen, appliedFilters]);

  // The API key is picked from its own dropdown on the page, not the filter panel.
  const activeFilterCount = Object.entries(appliedFilters).filter(([key, v]) => key !== 'apiKeyId' && v !== '').length;

  const [historyApiKey, setHistoryApiKey] = useState<string>("");

  // The history endpoint needs the X-API-Key of the key the email was sent with
  useEffect(() => {
    setHistoryApiKey("");
    const apiKeyId = selectedEmail?.recipientEmail?.apiKeyId;
    if (!apiKeyId) return;
    CustomerService.apiKey
      .getApiKeyValue(apiKeyId)
      .then(setHistoryApiKey)
      .catch((err) => {
        console.error("Failed to fetch API key:", err);
        toast.error("Failed to load email history");
        setSelectedEmail(null);
      });
  }, [selectedEmail]);

  const filterArgs = [
    appliedFilters.recipientEmails || undefined,
    appliedFilters.senderEmails || undefined,
    appliedFilters.subject || undefined,
    appliedFilters.status || undefined,
    appliedFilters.createdFrom || undefined,
    appliedFilters.createdTo || undefined,
  ] as const;

  const fetchEmails = async () => {
    if (!customerId) return;
    setIsLoading(true);
    try {
      // With an API key selected, list only that key's emails; otherwise the whole company's.
      const response = appliedFilters.apiKeyId
        ? await CustomerService.email.getCustomerEmailsByApiKeyId(
            parseInt(appliedFilters.apiKeyId), sortField, sortDirection === "desc", pageNumber, PAGE_SIZE, ...filterArgs,
          )
        : await CustomerService.email.getCustomerEmailsByCustomerId(
            parseInt(customerId), sortField, sortDirection === "desc", pageNumber, PAGE_SIZE, ...filterArgs,
          );
      setEmails(response.data.items || []);
      setTotalCount(response.data.totalCount || 0);
    } catch {
      setError("Failed to load email history");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!customerId) return;
    const timer = setTimeout(fetchEmails, 0);
    return () => clearTimeout(timer);
  }, [customerId, appliedFilters, sortField, sortDirection, pageNumber]);

  const handleTempFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTempFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setIsFilterOpen(false);
  };

  const handleClearAllFilters = () => {
    setTempFilters({ ...emptyFilters, apiKeyId: appliedFilters.apiKeyId });
    setAppliedFilters({ ...emptyFilters, apiKeyId: appliedFilters.apiKeyId });
  };

  const handleSort = (field: SortField) => {
    setSort(field, field === sortField && sortDirection === "asc" ? "desc" : "asc");
  };

  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <i className="bi bi-caret-up-fill ms-1"></i>
    ) : (
      <i className="bi bi-caret-down-fill ms-1"></i>
    );
  };

  const getStatusBadgeClass = (status: string): string => {
    const s = status.toLowerCase();
    if (s === "processed" || s === "queued" || s === "processing") return "warning";
    if (s === "sent" || s === "delivered" || s === "open" || s === "clicked") return "success";
    if (s === "dropped" || s === "bounce" || s === "deferred" || s === "rejection" || s === "blocked") return "danger";
    if (s === "spam reported" || s === "unsubscribe" || s === "group unsubscribe" || s === "group resubscribe" || s === "malformed") return "info";
    return "primary";
  };

  const handleBlockEmailAddress = async (address: string, shouldBlock: boolean, apiKeyId: number) => {
    try {
      const apiKey = await CustomerService.apiKey.getApiKeyValue(apiKeyId);
      await CustomerService.email.updateBlockStatus(
        { email: address, shouldBlock, reason: blockReason },
        apiKey,
      );
      toast.success(`Email address ${shouldBlock ? "blocked" : "unblocked"} successfully`);
      setBlockModalOpen(false);
      setBlockReason("");
      setSelectedEmailForBlock(null);
      fetchEmails();
    } catch {
      toast.error(`Failed to ${shouldBlock ? "block" : "unblock"} email address`);
    }
  };

  const openBlockModal = (email: string, isBlocked: boolean, apiKeyId: number) => {
    setSelectedEmailForBlock({ email, isBlocked, apiKeyId });
    setBlockModalOpen(true);
  };

  const handleExport = async (format: string) => {
    if (!customerId) return;
    setIsExporting(true);
    try {
      const response = appliedFilters.apiKeyId
        ? await CustomerService.email.exportEmailMessagesByApiKeyId(
            parseInt(appliedFilters.apiKeyId), sortField, sortDirection === "desc", ...filterArgs, format,
          )
        : await CustomerService.email.exportEmailMessagesByCustomerId(
            parseInt(customerId), sortField, sortDirection === "desc", ...filterArgs, format,
          );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `email-messages.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export emails");
    } finally {
      setIsExporting(false);
    }
  };

  if (error) {
    return <div className="alert alert-danger m-4" role="alert">{error}</div>;
  }

  return (
    <div className="container-fluid p-4">

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-1">Email Messages</h3>
          <p className="text-muted mb-0">View and manage email message history</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <ApiKeySelect
            id="email-messages-api-key"
            customerId={customerId}
            value={appliedFilters.apiKeyId}
            onChange={(apiKeyId) => setAppliedFilters({ ...appliedFilters, apiKeyId })}
          />
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setIsFilterOpen(true)}
            disabled={isLoading}
          >
            <i className="bi bi-funnel-fill me-1"></i>
            Filter
            {activeFilterCount > 0 && (
              <span className="badge bg-primary ms-1">{activeFilterCount}</span>
            )}
          </button>
          <ExportButton onExport={handleExport} isExporting={isExporting} size="sm" />
          <button className="btn btn-outline-primary btn-sm" onClick={fetchEmails} disabled={isLoading}>
            <i className={`${isLoading ? 'spinner-border spinner-border-sm' : 'bi bi-arrow-clockwise'} me-1`}></i>
            Refresh
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setIsSendOpen(true)}>
            <i className="bi bi-envelope-plus me-1"></i>Send Email
          </button>
        </div>
      </div>

      <Drawer isOpen={isSendOpen} onClose={() => setIsSendOpen(false)} title="Send Email" icon="envelope-plus" width={640}>
        <SendEmailSidebar
          customerId={customerId ?? ""}
          defaultApiKeyId={appliedFilters.apiKeyId}
          onClose={() => setIsSendOpen(false)}
          onSend={fetchEmails}
        />
      </Drawer>

      <FilterDrawer
        title="Filter Email Messages"
        width={500}
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onClear={handleClearAllFilters}
        onApply={handleApplyFilters}
      >
        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Recipient Email</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by recipient..."
              name="recipientEmails"
              value={tempFilters.recipientEmails}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Sender Email</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by sender..."
              name="senderEmails"
              value={tempFilters.senderEmails}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Subject</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by subject..."
              name="subject"
              value={tempFilters.subject}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Status</label>
          <select
            className="form-select"
            name="status"
            value={tempFilters.status}
            onChange={handleTempFilterChange}
          >
            <option value="">All</option>
            <option value="queued">Queued</option>
            <option value="processed">Processed</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="open">Open</option>
            <option value="clicked">Clicked</option>
            <option value="bounce">Bounce</option>
            <option value="dropped">Dropped</option>
            <option value="deferred">Deferred</option>
            <option value="blocked">Blocked</option>
            <option value="spam reported">Spam Reported</option>
            <option value="unsubscribe">Unsubscribe</option>
          </select>
        </div>

        <hr className="my-3" />

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Date From</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
            <input
              type="date"
              className="form-control"
              name="createdFrom"
              value={tempFilters.createdFrom}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="form-label fw-bold small text-muted">Date To</label>
          <div className="input-group">
            <span className="input-group-text"><i className="bi bi-calendar-event"></i></span>
            <input
              type="date"
              className="form-control"
              name="createdTo"
              value={tempFilters.createdTo}
              onChange={handleTempFilterChange}
            />
          </div>
        </div>
      </FilterDrawer>

      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : emails.length === 0 ? (
        <div className="alert alert-info">
          {activeFilterCount > 0 ? "No emails match your search criteria." : "No email history found."}
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  <th onClick={() => handleSort("recipientEmail")} style={{ cursor: "pointer" }}>
                    Recipient {renderSortIndicator("recipientEmail")}
                  </th>
                  <th onClick={() => handleSort("senderEmail")} style={{ cursor: "pointer" }}>
                    Sender {renderSortIndicator("senderEmail")}
                  </th>
                  <th onClick={() => handleSort("subject")} style={{ cursor: "pointer" }}>
                    Subject {renderSortIndicator("subject")}
                  </th>
                  <th onClick={() => handleSort("lastStatus")} style={{ cursor: "pointer" }}>
                    Status {renderSortIndicator("lastStatus")}
                  </th>
                  <th onClick={() => handleSort("createdAt")} style={{ cursor: "pointer" }}>
                    Date {renderSortIndicator("createdAt")}
                  </th>
                  <th>API Key</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {emails.map((email) => (
                  <tr key={email.id}>
                    <td>{email.recipientEmail?.address}</td>
                    <td>{email.senderEmail}</td>
                    <td>{email.subject}</td>
                    <td>
                      <span className={`badge bg-${getStatusBadgeClass(email.lastStatus)}`}>
                        {email.lastStatus}
                      </span>
                    </td>
                    <td>{email.createdAt ? new Date(email.createdAt).toLocaleString() : "N/A"}</td>
                    <td className="text-nowrap">{apiKeyLabel(apiKeyNames, sentWithApiKeyId(email))}</td>
                    <td>
                      <div className="btn-group">
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => setSelectedEmail(email)}
                          title="View History"
                        >
                          <i className="bi bi-clock-history"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => setPreviewEmail(email)}
                          title="Preview Email"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => openBlockModal(email.recipientEmail?.address || "", email.recipientEmail?.isBlocked || false, email.recipientEmail?.apiKeyId)}
                          title={email.recipientEmail?.isBlocked ? `Blocked: ${email.recipientEmail?.blockedReason || "No reason provided"}` : "Block Email"}
                        >
                          <i className={`bi bi-${email.recipientEmail?.isBlocked ? "unlock" : "lock"}`}></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination pageNumber={pageNumber} pageSize={PAGE_SIZE} totalCount={totalCount} onPageChange={setPageNumber} />
        </>
      )}

      {/* Email Preview Modal */}
      {previewEmail && (
        <div className="modal fade show" style={{ display: "block" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Email Preview: {previewEmail.subject}</h5>
                <button type="button" className="btn-close" onClick={() => setPreviewEmail(null)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3"><strong>From:</strong> {previewEmail.senderEmail}</div>
                <div className="mb-3"><strong>To:</strong> {previewEmail.recipientEmail?.address}</div>
                <div className="mb-3"><strong>Subject:</strong> {previewEmail.subject}</div>
                <div className="border rounded p-3 email-preview-content">
                  <div dangerouslySetInnerHTML={{ __html: previewEmail.htmlContent }} className="email-content" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPreviewEmail(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Block/Unblock Modal */}
      {blockModalOpen && selectedEmailForBlock && (
        <div className="modal fade show" style={{ display: "block" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedEmailForBlock.isBlocked ? "Unblock Email" : "Block Email"}</h5>
                <button type="button" className="btn-close" onClick={() => { setBlockModalOpen(false); setSelectedEmailForBlock(null); setBlockReason(""); }}></button>
              </div>
              <div className="modal-body">
                {!selectedEmailForBlock.isBlocked && (
                  <div className="mb-3">
                    <label htmlFor="blockReason" className="form-label">Reason for blocking</label>
                    <textarea
                      className="form-control"
                      id="blockReason"
                      rows={3}
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      placeholder="Enter reason for blocking this email address"
                      required
                    />
                  </div>
                )}
                <p>
                  Are you sure you want to {selectedEmailForBlock.isBlocked ? "unblock" : "block"} the email address{" "}
                  <strong>{selectedEmailForBlock.email}</strong>?
                </p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setBlockModalOpen(false); setSelectedEmailForBlock(null); setBlockReason(""); }}>Cancel</button>
                <button
                  type="button"
                  className={`btn btn-${selectedEmailForBlock.isBlocked ? "success" : "danger"}`}
                  onClick={() => handleBlockEmailAddress(selectedEmailForBlock.email, !selectedEmailForBlock.isBlocked, selectedEmailForBlock.apiKeyId)}
                  disabled={!selectedEmailForBlock.isBlocked && !blockReason.trim()}
                >
                  {selectedEmailForBlock.isBlocked ? "Unblock" : "Block"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          .email-preview-content { background-color: #ffffff; max-height: 70vh; overflow-y: auto; }
          .email-content { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px; line-height: 1.5; color: #212529; }
          .email-content img { max-width: 100%; height: auto; }
          .email-content table { border-collapse: collapse; width: 100%; margin-bottom: 1rem; }
          .email-content table td, .email-content table th { border: 1px solid #dee2e6; padding: 0.75rem; }
          .email-content a { color: #0d6efd; text-decoration: none; }
          .email-content a:hover { text-decoration: underline; }
          .email-content p { margin-bottom: 1rem; }
        `}
      </style>

      {selectedEmail && historyApiKey && (
        <EmailHistorySidebar
          isOpen={true}
          onClose={() => setSelectedEmail(null)}
          emailId={selectedEmail.id}
          subject={selectedEmail.subject}
          apiKey={historyApiKey}
        />
      )}
    </div>
  );
};

export default CustomerEmailMessages;
